import { Defect } from '@/app/data/types'
import { isPreviousShape } from '@/app/util'
import { useAllTaskStore } from '@/app/store/task'
import { getCurrentColorName } from '@/app/constant'
import { getDefaultBalloonColors, getRepairBalloonColors } from '@/app/function/helper'

// 결함의 표시 모드. 색상칩/넘버링/캔버스 보라색/동그라미 분기의 단일 진실의 원천.
// - 'current': 일반 현재차 결함, 또는 is_current=1로 강제 현차가 된 전차
// - 'becameCurrent': 전차 + 현차사진 있음 + is_current=0 (사진이 들어가 시각적으로 현차처럼 그려야 함)
// - 'previous': 순수 전차 (보라색)
export type DefectDisplayMode = 'current' | 'becameCurrent' | 'previous'

type DefectDisplayInput = Pick<Defect, 'previous_defect_id' | 'is_current'>

/**
 * 결함의 표시 모드 판정.
 * is_current === 1 이면 사진/progressing/색상과 무관하게 항상 'current'.
 * @param shape - previous_defect_id, is_current 만 있으면 충분
 * @param hasCurrentPhoto - 호출자가 적절한 store에서 미리 조회한 현차사진 유무
 */
export function getDefectDisplayMode(
  shape: DefectDisplayInput,
  hasCurrentPhoto: boolean,
): DefectDisplayMode {
  // is_current는 store/플랫폼에 따라 number(1) 또는 boolean(true)로 들어올 수 있어 truthy 비교
  if (shape.is_current) return 'current'
  if (!isPreviousShape(shape.previous_defect_id)) return 'current'
  return hasCurrentPhoto ? 'becameCurrent' : 'previous'
}

/**
 * 색상칩/리스트가 '보라색으로 그릴지' 결정하는 단일 헬퍼.
 * 기존 isPrevWithoutCurrentPhoto 의 의미를 is_current 까지 고려해 일원화.
 */
export function isEffectivelyPrevious(
  shape: DefectDisplayInput,
  hasCurrentPhoto: boolean,
): boolean {
  return getDefectDisplayMode(shape, hasCurrentPhoto) === 'previous'
}

export interface SafeDisplayColors {
  borderColor: string
  fontColor: string
  fillColor: string
}

/**
 * 잘못 저장된 결함(mode != 'previous'인데 store의 색상이 보라색 등으로 꼬임)의
 * 안전한 표시 색상을 계산. 우선순위:
 *   1. progressing=2 → 보수완료색 또는 기본색
 *   2. savedCurrentStyles 존재 → 그 색상 (보라 값은 fallback)
 *   3. fallback 기본색
 *
 * @param shape - 보수완료 분기에 progressing 필드 사용
 * @param fallback - savedCurrentStyles 없거나 보라일 때 쓸 기본색 (canvasStore.getCurrentCreateStyles에서 받음)
 */
export function resolveSafeDisplayColors(
  shape: Defect,
  fallback: SafeDisplayColors,
): SafeDisplayColors {
  const config = useAllTaskStore().userSettingConfig
  if (Number(shape.progressing) === 2) {
    const colors = getRepairBalloonColors(config) ?? getDefaultBalloonColors(config)
    return {
      borderColor: colors.borderColor,
      fontColor: colors.fontColor,
      fillColor: colors.fillColor,
    }
  }
  const saved = shape.shape_data?.savedCurrentStyles
  const safeNonPurple = (v: string | undefined, fb: string): string =>
    v && getCurrentColorName(v) !== 'purple' ? v : fb
  return {
    borderColor: safeNonPurple(saved?.color, fallback.borderColor),
    fontColor: safeNonPurple(saved?.balloonFontColor, fallback.fontColor),
    fillColor: saved?.balloonFillColor ?? fallback.fillColor,
  }
}

/**
 * 도형이 아직 없는 전차 결함(보고서 가져오기로 생성된 NO_SHAPE 전차) 판정.
 * - 수동/보고서 전차 규약(previous_defect_id === 0)이면서 shape_data 가 없거나 NO_SHAPE
 *   (전차복사(copy-as-previous)로 생긴 무도형 전차는 previous_defect_id > 0 이라 제외 —
 *    포함하면 일반 사용자 과업에서도 자동 전차 등록 모드·뱃지가 켜진다)
 * - 현차 사진이 붙어 현차로 승격된 전차는 제외(일반 목록의 pending-link 경로가 담당)
 * @param hasCurrentPhoto - 호출자가 store 에서 조회한 현차사진 유무(기본 false).
 *   함수로 넘기면 앞 조건을 모두 통과한 행에서만 호출한다 — 사진 조회(store GetItem)가 비싸서
 *   층 전체를 도는 computed 에서는 지연 평가로 넘길 것.
 */
export function isUnplacedPrevDefect(
  d: Defect,
  hasCurrentPhoto: boolean | (() => boolean) = false,
): boolean {
  if (!d || d.deleted_at) return false
  if (d.template === 'canvas-brush') return false
  if (Number(d.parent_defect_id)) return false
  if (d.previous_defect_id === null || d.previous_defect_id === undefined) return false
  if (Number(d.previous_defect_id) !== 0) return false
  if (d.shape_data && d.shape_type !== 'NO_SHAPE') return false
  const photo = typeof hasCurrentPhoto === 'function' ? hasCurrentPhoto() : hasCurrentPhoto
  return getDefectDisplayMode(d, photo) === 'previous'
}

/** 층(floorplan_id)별 도형 없는 전차 건수 */
export function countUnplacedPrevByFloorplan(
  shapes: Defect[],
  hasCurrentPhoto: (shapeId: string) => boolean = () => false,
): Map<string, number> {
  const map = new Map<string, number>()
  for (const d of shapes) {
    if (!isUnplacedPrevDefect(d, () => hasCurrentPhoto(`${d.id}`))) continue
    const key = `${d.floorplan_id}`
    map.set(key, (map.get(key) ?? 0) + 1)
  }
  return map
}

/**
 * 시설물별 도형 없는 전차 건수.
 * @param floorplanToFacility - floorplan_id → facility_id 매핑(과업 트리에서 생성)
 */
export function countUnplacedPrevByFacility(
  shapes: Defect[],
  floorplanToFacility: Map<string, string>,
  hasCurrentPhoto: (shapeId: string) => boolean = () => false,
): Map<string, number> {
  const byFloor = countUnplacedPrevByFloorplan(shapes, hasCurrentPhoto)
  const map = new Map<string, number>()
  for (const [floorId, count] of byFloor) {
    const facilityId = floorplanToFacility.get(floorId)
    if (!facilityId) continue
    map.set(facilityId, (map.get(facilityId) ?? 0) + count)
  }
  return map
}
